import { AssetAmount } from "@sundaeswap/asset";
import { ITxBuilder, ITxBuilderSubmit } from "@sundaeswap/core";
import { TSwapOutcome } from "@sundaeswap/cpp";

import { Dispatch, ReactNode } from "react";
import { TransactionErrorHandler } from "../../utils/transactions/TransactionErrorHandler.class";
import {
  EExchangeActions,
  ETradeWarning,
  ETransactionBuilderState,
  IExchangeAction,
  ITransactionErrorActions,
  TInterval,
} from "./types";

type TActionFunc<T> = (value: T, dispatch: Dispatch<IExchangeAction>) => void;

const setActiveInterval: TActionFunc<TInterval> = (activeInterval, dispatch) =>
  dispatch({ type: EExchangeActions.SET_ACTIVE_INTERVAL, payload: { activeInterval } });

const setAssetChangeScope: TActionFunc<"activeAssetA" | "activeAssetB"> = (
  assetChangeScope,
  dispatch
) => dispatch({ type: EExchangeActions.SET_ASSET_CHANGE_SCOPE, payload: { assetChangeScope } });

const setDisplayElements: TActionFunc<boolean> = (displayElements, dispatch) =>
  dispatch({ type: EExchangeActions.SET_DISPLAY_ELEMENTS, payload: { displayElements } });

const setTransactionErrorMessage: TActionFunc<ReactNode> = (transactionErrorMessage, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_ERROR_MESSAGE,
    payload: { transactionErrorMessage },
  });

const setTransactionErrorHandler: TActionFunc<TransactionErrorHandler> = (
  transactionErrorHandler,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_ERROR_HANDLER,
    payload: { transactionErrorHandler },
  });

const setTransactionErrorActions: TActionFunc<ITransactionErrorActions | undefined> = (
  transactionErrorActions,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_ERROR_ACTIONS,
    payload: { transactionErrorActions },
  });

const setFormattedLimitValue: TActionFunc<string> = (formattedLimitValue, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_FORMATTED_LIMIT_VALUE,
    payload: { formattedLimitValue },
  });

const setFormattedValueAssetA: TActionFunc<string | undefined> = (formattedValueAssetA, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_FORMATTED_VALUE_ASSET_A,
    payload: { formattedValueAssetA },
  });

const setFormattedValueAssetB: TActionFunc<string> = (formattedValueAssetB, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_FORMATTED_VALUE_ASSET_B,
    payload: { formattedValueAssetB },
  });

const setIsAssetConsentChecked: TActionFunc<boolean> = (isAssetConsentChecked, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_IS_ASSET_CONSENT_CHECKED,
    payload: { isAssetConsentChecked },
  });

const setOutputExceedsReserve: TActionFunc<boolean> = (outputExceedsReserve, dispatch) =>
  dispatch({
    type: EExchangeActions.SET_OUTPUT_EXCEEDS_RESERVE,
    payload: { outputExceedsReserve },
  });

const setIsInsufficientA: TActionFunc<boolean> = (isInsufficientAssetA, dispatch) =>
  dispatch({ type: EExchangeActions.SET_IS_INSUFFICIENT_A, payload: { isInsufficientAssetA } });

const setIsInsufficientB: TActionFunc<boolean> = (isInsufficientAssetB, dispatch) =>
  dispatch({ type: EExchangeActions.SET_IS_INSUFFICIENT_B, payload: { isInsufficientAssetB } });

const setIsRemainingAdaBelowThreshold: TActionFunc<boolean> = (
  isRemainingAdaAmountBelowThreshold,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_IS_REMAINING_ADA_BELOW_THRESHOLD,
    payload: { isRemainingAdaAmountBelowThreshold },
  });

const setLimitValue: TActionFunc<string | undefined> = (limitValue, dispatch) =>
  dispatch({ type: EExchangeActions.SET_LIMIT_VALUE, payload: { limitValue } });

const setParity: TActionFunc<AssetAmount> = (parity, dispatch) =>
  dispatch({ type: EExchangeActions.SET_PARITY, payload: { parity } });

const setSwapOutcome: TActionFunc<TSwapOutcome> = (swapOutcome, dispatch) =>
  dispatch({ type: EExchangeActions.SET_SWAP_OUTCOME, payload: { swapOutcome } });

const setTradeWarning: TActionFunc<ETradeWarning> = (tradeWarning, dispatch) =>
  dispatch({ type: EExchangeActions.SET_TRADE_WARNING, payload: { tradeWarning } });

const setTransactionBuilderBuiltTx: TActionFunc<ITxBuilder | undefined> = (
  transactionBuilderBuiltTx,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_BUILDER_BUILT_TX,
    payload: { transactionBuilderBuiltTx },
  });

const setTransactionBuilderCompleteTx: TActionFunc<ITxBuilderSubmit | undefined> = (
  transactionBuilderCompleteTx,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_BUILDER_COMPLETE_TX,
    payload: { transactionBuilderCompleteTx },
  });

const setTransactionBuilderState: TActionFunc<ETransactionBuilderState> = (
  transactionBuilderState,
  dispatch
) =>
  dispatch({
    type: EExchangeActions.SET_TRANSACTION_BUILDER_STATE,
    payload: { transactionBuilderState },
  });

const setValueAssetA: TActionFunc<string | undefined> = (valueAssetA, dispatch) =>
  dispatch({ type: EExchangeActions.SET_VALUE_ASSET_A, payload: { valueAssetA } });

const setValueAssetB: TActionFunc<string> = (valueAssetB, dispatch) =>
  dispatch({ type: EExchangeActions.SET_VALUE_ASSET_B, payload: { valueAssetB } });

const setCurrentPageAssetList: TActionFunc<number> = (page, dispatch) =>
  dispatch({ type: EExchangeActions.SET_CURRENT_PAGE, payload: { page } });

const increasePageAssetList = (dispatch: Dispatch<IExchangeAction>) =>
  dispatch({ type: EExchangeActions.INCREASE_PAGE });

/* -------------------------------------------------------------------------------------------------
 * Export
 * -----------------------------------------------------------------------------------------------*/
export const ExchangeActions = {
  increasePageAssetList,
  setActiveInterval,
  setAssetChangeScope,
  setCurrentPageAssetList,
  setDisplayElements,
  setTransactionErrorActions,
  setTransactionErrorHandler,
  setTransactionErrorMessage,
  setFormattedLimitValue,
  setFormattedValueAssetA,
  setFormattedValueAssetB,
  setOutputExceedsReserve,
  setIsAssetConsentChecked,
  setIsInsufficientA,
  setIsInsufficientB,
  setIsRemainingAdaBelowThreshold,
  setLimitValue,
  setParity,
  setSwapOutcome,
  setTradeWarning,
  setTransactionBuilderBuiltTx,
  setTransactionBuilderCompleteTx,
  setTransactionBuilderState,
  setValueAssetA,
  setValueAssetB,
};
